import { Link } from "react-router-dom";
import { Check, ArrowLeft, Crown } from "lucide-react";
import { motion } from "framer-motion";
import StoneLogo from "@/components/StoneLogo";
import { Button } from "@/components/ui/button";

const PLANS = [
  {
    name: "Free",
    price: "R$ 0",
    period: "/mês",
    desc: "Para descobrir a inteligência mineral.",
    features: [
      "15 perguntas por dia",
      "Análise de até 3 imagens por dia",
      "Histórico básico de conversas",
      "Acesso à biblioteca pública",
    ],
    cta: "Começar grátis",
    href: "/chat",
    highlight: false,
  },
  {
    name: "Pro",
    price: "R$ 89",
    period: "/mês",
    desc: "A StoneIA sem limites.",
    features: [
      "Perguntas ilimitadas",
      "Análise visual avançada (alta resolução)",
      "Leitura completa de catálogos PDF",
      "Comparação de materiais lado a lado",
      "Memória técnica estendida",
      "Acesso prioritário à biblioteca completa",
      "Suporte premium",
    ],
    cta: "Começar Pro",
    href: "/chat",
    highlight: true,
  },
  {
    name: "Enterprise",
    price: "Sob consulta",
    period: "",
    desc: "Para escritórios, marmorarias e construtoras.",
    features: [
      "Tudo do Pro",
      "Acesso multi-usuário",
      "API mineral privada",
      "RAG sobre catálogo próprio",
      "Onboarding e treinamento dedicado",
    ],
    cta: "Falar com vendas",
    href: "#",
    highlight: false,
  },
];

const ProPage = () => (
  <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="pro-page">
    <header className="border-b border-[#1f1f1f] backdrop-blur-xl bg-[#0A0A0A]/70 sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="pro-back-link">
          <StoneLogo size={44} wordmarkSize={24} />
        </Link>
        <Link to="/chat" data-testid="pro-launch-chat">
          <Button className="rounded-full bg-[#F5F5F7] text-black hover:bg-white px-5 h-9 text-[13px] font-medium">
            Abrir Chat
          </Button>
        </Link>
      </div>
    </header>

    <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-12 text-center">
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-[11px] tracking-[0.32em] uppercase text-zinc-500 mb-6"
      >
        Planos · Edição Pro
      </motion.p>
      <motion.h1
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.05 }}
        className="silver-text text-5xl md:text-6xl font-light tracking-tighter"
        style={{ fontFamily: "Outfit" }}
      >
        Inteligência mineral sem limites.
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.15 }}
        className="text-zinc-400 mt-5 max-w-2xl mx-auto"
      >
        Escolha o plano ideal para projetar, especificar e diagnosticar pedras decorativas com precisão profissional.
      </motion.p>
    </section>

    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
      <div className="grid md:grid-cols-3 gap-6">
        {PLANS.map((p, i) => (
          <motion.div
            key={p.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 + i * 0.08 }}
            className={`relative rounded-3xl p-8 border ${
              p.highlight
                ? "border-[#3a3a3a] bg-gradient-to-b from-[#161616] to-[#0e0e0e]"
                : "border-[#1f1f1f] bg-[#0F0F10]"
            }`}
            data-testid={`plan-${p.name.toLowerCase()}`}
          >
            {p.highlight && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F5F5F7] text-black text-[10.5px] tracking-[0.18em] uppercase px-3 py-1 rounded-full flex items-center gap-1.5 font-medium">
                <Crown size={11} /> Mais escolhido
              </div>
            )}
            <h3 className="text-2xl font-light tracking-tight" style={{ fontFamily: "Outfit" }}>
              {p.name}
            </h3>
            <p className="text-zinc-500 text-[13px] mt-2 mb-6 min-h-[40px]">{p.desc}</p>
            <div className="flex items-baseline gap-1 mb-8">
              <span className="text-4xl font-light text-white" style={{ fontFamily: "Outfit" }}>
                {p.price}
              </span>
              <span className="text-zinc-500 text-[13px]">{p.period}</span>
            </div>
            <ul className="space-y-3 mb-8">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-[13.5px] text-zinc-300">
                  <Check size={14} className="mt-0.5 text-zinc-200 shrink-0" strokeWidth={1.6} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Link to={p.href} data-testid={`plan-cta-${p.name.toLowerCase()}`}>
              <Button
                className={`w-full h-11 rounded-full text-[13.5px] font-medium ${
                  p.highlight
                    ? "bg-[#F5F5F7] text-black hover:bg-white"
                    : "bg-[#1a1a1a] text-white hover:bg-[#222] border border-[#262626]"
                }`}
              >
                {p.cta}
              </Button>
            </Link>
          </motion.div>
        ))}
      </div>
      <div className="text-center text-zinc-600 text-[11.5px] mt-12 tracking-wide" data-testid="pro-disclaimer">
        Preços ilustrativos. Cobrança e pagamento serão habilitados em uma próxima fase.
      </div>
    </section>
  </div>
);

export default ProPage;
